(() => {
  const roots = document.querySelectorAll('.bar-series-landing');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover)').matches;
  const numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'ja-JP');

  roots.forEach(root => {
    if (root.dataset.barSeriesLandingInit === 'true') return;
    root.dataset.barSeriesLandingInit = 'true';

    const nav = root.querySelector('#bsl-nav');
    const heroBg = root.querySelector('#bsl-heroBg');
    const progress = root.querySelector('#bsl-progress');
    const videoShell = root.querySelector('#bsl-videoShell');
    const dock = root.querySelector('#bsl-dock');
    const dockLinks = dock ? [...dock.querySelectorAll('a')] : [];
    const dockSections = dockLinks.map(link => root.querySelector(link.getAttribute('href')));
    let scrollTicking = false;

    const updateScrollEffects = () => {
      const y = window.scrollY;
      nav?.classList.toggle('scrolled', y > 40);

      if (heroBg && !reduceMotion && window.innerWidth > 640 && y < window.innerHeight) {
        heroBg.style.transform = `translate3d(0, ${y * 0.22}px, 0)`;
      }

      if (progress) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        progress.style.width = `${max > 0 ? (y / max) * 100 : 0}%`;
      }

      if (videoShell && !reduceMotion) {
        const rect = videoShell.getBoundingClientRect();
        if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
          const midpoint = rect.top + rect.height / 2;
          const distance = Math.abs(midpoint - window.innerHeight / 2) / (window.innerHeight / 2);
          const scale = 1.02 - Math.min(distance, 1) * 0.07;
          videoShell.style.transform = `scale(${scale})`;
        }
      }

      if (dock) {
        dock.classList.toggle('show', y > window.innerHeight * 0.6);
        let activeIndex = -1;
        dockSections.forEach((section, index) => {
          if (section && section.offsetTop <= y + window.innerHeight * 0.4) activeIndex = index;
        });
        dockLinks.forEach((link, index) => {
          const active = index === activeIndex;
          link.classList.toggle('active', active);
          if (active) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      }

      scrollTicking = false;
    };

    const onScroll = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      window.requestAnimationFrame(updateScrollEffects);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    updateScrollEffects();

    /* Reveal elements, while leaving content visible when motion is reduced. */
    const revealElements = root.querySelectorAll('.reveal');
    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealElements.forEach(element => element.classList.add('in'));
    } else {
      const revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

      revealElements.forEach(element => {
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          element.style.transition = 'none';
          element.classList.add('in');
          requestAnimationFrame(() => requestAnimationFrame(() => { element.style.transition = ''; }));
        } else {
          revealObserver.observe(element);
        }
      });
    }

    /* Counters retain their real HTML values for SEO/no-JS, then animate once in view. */
    const counters = root.querySelectorAll('.count[data-target]');
    if (!reduceMotion && 'IntersectionObserver' in window) {
      const countObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          countObserver.unobserve(entry.target);
          const element = entry.target;
          const target = Number(element.dataset.target);
          if (!Number.isFinite(target)) return;
          const duration = 1200;
          const startTime = performance.now();
          element.textContent = '0';

          const tick = now => {
            const progressValue = Math.min((now - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progressValue, 3);
            element.textContent = numberFormat.format(Math.round(target * eased));
            if (progressValue < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      }, { threshold: 0.6 });
      counters.forEach(counter => countObserver.observe(counter));
    }

    /* Darth SaBar divider ignition. */
    const saber = root.querySelector('#bsl-saber');
    if (saber) {
      if (reduceMotion || !('IntersectionObserver' in window)) {
        saber.classList.add('lit');
      } else {
        const saberObserver = new IntersectionObserver((entries, observer) => {
          if (entries.some(entry => entry.isIntersecting)) {
            saber.classList.add('lit');
            observer.disconnect();
          }
        }, { threshold: 0.5 });
        saberObserver.observe(saber.parentElement);
      }
    }

    /* FusionCore finish comparison: pointer, swatches, and keyboard. */
    const colorStage = root.querySelector('#bsl-fcStage');
    const colorCta = root.querySelector('#bsl-fcCta');
    const colorName = root.querySelector('#bsl-colorName');
    const swatches = [...root.querySelectorAll('.swatch[data-color]')];
    const colorCopy = {
      brorange: ['Brorange', 'ブロンズ×オレンジ。ローンチムービーでも使用された、存在感あふれるカラー。'],
      silver: ['Silver', '落ち着いたシルバー。素材感を引き立てる、クリーンで普遍的な仕上げ。']
    };
    const colorUrls = {
      brorange: colorStage?.dataset.brorangeUrl || '/products/fusioncore-bar?variant=45884095103112',
      silver: colorStage?.dataset.silverUrl || '/products/fusioncore-bar'
    };
    let colorValue = 72;
    let activeColor = '';
    let dragging = false;

    const renderColor = (percentage, animate = false) => {
      if (!colorStage) return;
      colorValue = Math.max(0, Math.min(100, percentage));
      const color = colorValue >= 50 ? 'brorange' : 'silver';
      colorStage.classList.toggle('animating', animate);
      colorStage.classList.toggle('brorange-on', color === 'brorange');
      colorStage.style.setProperty('--w', `${colorValue}%`);
      colorStage.setAttribute('aria-valuenow', String(Math.round(colorValue)));
      colorStage.setAttribute('aria-valuetext', colorCopy[color][0]);

      if (color !== activeColor) {
        activeColor = color;
        if (colorCta) colorCta.href = colorUrls[color];
        if (colorName) {
          colorName.replaceChildren(document.createTextNode(colorCopy[color][0]));
          const description = document.createElement('small');
          description.textContent = colorCopy[color][1];
          colorName.appendChild(description);
        }
        swatches.forEach(button => {
          const selected = button.dataset.color === color;
          button.classList.toggle('active', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
      }
    };

    const setColorFromPointer = event => {
      if (!colorStage) return;
      const rect = colorStage.getBoundingClientRect();
      renderColor(((event.clientX - rect.left) / rect.width) * 100, false);
    };

    colorStage?.addEventListener('pointerdown', event => {
      dragging = true;
      setColorFromPointer(event);
      try { colorStage.setPointerCapture(event.pointerId); } catch (_) {}
    });
    colorStage?.addEventListener('pointermove', event => {
      if (dragging) setColorFromPointer(event);
    });
    ['pointerup', 'pointercancel'].forEach(eventName => {
      colorStage?.addEventListener(eventName, () => { dragging = false; });
    });
    colorStage?.addEventListener('keydown', event => {
      const steps = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5 };
      if (event.key in steps) {
        event.preventDefault();
        renderColor(colorValue + steps[event.key], true);
      } else if (event.key === 'Home') {
        event.preventDefault();
        renderColor(0, true);
      } else if (event.key === 'End') {
        event.preventDefault();
        renderColor(100, true);
      }
    });
    swatches.forEach(button => {
      button.addEventListener('click', () => renderColor(button.dataset.color === 'brorange' ? 100 : 0, true));
    });
    renderColor(colorValue, false);

    /* Comparison column hover, only on devices that actually hover. */
    if (canHover) {
      root.querySelectorAll('[data-col]').forEach(cell => {
        cell.addEventListener('mouseenter', () => {
          root.querySelectorAll(`[data-col="${cell.dataset.col}"]`).forEach(item => item.classList.add('compare-col-hover'));
        });
        cell.addEventListener('mouseleave', () => {
          root.querySelectorAll(`[data-col="${cell.dataset.col}"]`).forEach(item => item.classList.remove('compare-col-hover'));
        });
      });
    }

    /* Brorange product tilt. */
    const tiltZone = root.querySelector('#bsl-tiltZone');
    const tiltBar = root.querySelector('#bsl-tiltBar');
    if (canHover && !reduceMotion && tiltZone && tiltBar) {
      tiltZone.addEventListener('pointermove', event => {
        const rect = tiltZone.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        tiltBar.style.transform = `rotateY(${x * 14}deg) rotateX(${-y * 10}deg) translateZ(20px) scale(1.02)`;
      });
      tiltZone.addEventListener('pointerleave', () => { tiltBar.style.transform = ''; });
    }

    /* Launch film. */
    const filmVideo = root.querySelector('#bsl-filmVideo');
    const soundButton = root.querySelector('#bsl-soundBtn');
    if (filmVideo && videoShell && 'IntersectionObserver' in window) {
      const filmObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !document.hidden) filmVideo.play().catch(() => {});
          else filmVideo.pause();
        });
      }, { threshold: 0.35 });
      filmObserver.observe(videoShell);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) filmVideo.pause();
      });
    }
    soundButton?.addEventListener('click', () => {
      if (!filmVideo) return;
      filmVideo.muted = !filmVideo.muted;
      const soundOn = !filmVideo.muted;
      soundButton.textContent = soundOn ? '音声オン' : '音声オフ';
      soundButton.setAttribute('aria-pressed', String(soundOn));
      if (soundOn) filmVideo.play().catch(() => {});
    });

    /* Exercise pills animate only while the group is visible. */
    const pills = [...root.querySelectorAll('#bsl-movePills .pill')];
    let pillTimer = 0;
    let pillIndex = 0;
    const stopPills = () => {
      if (pillTimer) window.clearInterval(pillTimer);
      pillTimer = 0;
    };
    const startPills = () => {
      if (!pills.length || pillTimer || reduceMotion) return;
      pillTimer = window.setInterval(() => {
        if (!root.isConnected) return stopPills();
        pills.forEach(pill => pill.classList.remove('lit'));
        pills[pillIndex % pills.length].classList.add('lit');
        pillIndex += 1;
      }, 1400);
    };
    if (pills.length && !reduceMotion && 'IntersectionObserver' in window) {
      const pillObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.isIntersecting ? startPills() : stopPills());
      }, { threshold: 0.2 });
      pillObserver.observe(root.querySelector('#bsl-movePills'));
    }

    /* Cursor spotlight and magnetic buttons are desktop-only details. */
    if (canHover && !reduceMotion) {
      root.querySelectorAll('.feature-card,.lineup-card,.benefit,.aluma-stat,.box-card,.voltra-card').forEach(card => {
        card.classList.add('glow-card');
        card.addEventListener('pointermove', event => {
          const rect = card.getBoundingClientRect();
          card.style.setProperty('--mx', `${event.clientX - rect.left}px`);
          card.style.setProperty('--my', `${event.clientY - rect.top}px`);
        });
      });

      root.querySelectorAll('.btn-primary,.btn-sith').forEach(button => {
        button.addEventListener('pointermove', event => {
          const rect = button.getBoundingClientRect();
          const x = (event.clientX - rect.left) / rect.width - 0.5;
          const y = (event.clientY - rect.top) / rect.height - 0.5;
          button.style.transform = `translate(${x * 7}px, ${y * 5 - 2}px) scale(1.02)`;
        });
        button.addEventListener('pointerleave', () => { button.style.transform = ''; });
      });
    }
  });
})();
